import React, { useContext, useEffect, useState } from "react";
import { CanvasContext } from "../../../contexts/CanvasContext";

const TableComponent = ({ table = {} }) => {
  const { hoveredDivision, files } = useContext(CanvasContext);
  const isHovered = table.id === hoveredDivision;
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = table;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (table.backgroundImage && table.backgroundImage.startsWith("./")) {
        const imageName = table.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(table.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [table.backgroundImage, files]);

  const tableStyle = {
    width: table.width || "fit-content",
    height: table.height || "auto",

    margin: table.margin || "0px",
    marginTop: table.marginTop || table.margin || "0",
    marginRight: table.marginRight || table.margin || "5px",
    marginBottom: table.marginBottom || table.margin || "0",
    marginLeft: table.marginLeft || table.margin || "0",
    padding: table.padding || "0px",
    paddingTop: table.paddingTop || table.padding || "0",
    paddingRight: table.paddingRight || table.padding || "0",
    paddingBottom: table.paddingBottom || table.padding || "0",
    paddingLeft: table.paddingLeft || table.padding || "0",

    borderTop: isHovered
      ? "1px dashed #555 "
      : table.borderTop || table.border || "none",
    borderRight: isHovered
      ? "1px dashed #555 "
      : table.borderRight || table.border || "none",
    borderBottom: isHovered
      ? "1px dashed #555 "
      : table.borderBottom || table.border || "none",
    borderLeft: isHovered
      ? "1px dashed #555 "
      : table.borderLeft || table.border || "none",

    borderSapcing: table.borderSpacing || "0px",
    borderCollapse: table.borderCollapse || "separate",
    borderRadius: table.borderRadius || "0",
    outline: table.outline || "none",

    position: table.position || "static",
    top: table.top || "auto",
    right: table.right || "auto",
    bottom: table.bottom || "auto",
    left: table.left || "auto",
    zIndex: table.zIndex || "auto",

    backgroundColor: table.backgroundColor || "#ffffff",
    color: table.color || "#000000",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),
    backgroundSize: table.backgroundSize || "auto",
    backgroundPosition: table.backgroundPosition || "0% 0%",
    backgroundRepeat: table.backgroundRepeat || "repeat",
    backgroundAttachment: table.backgroundAttachment || "scroll",

    boxShadow: table.boxShadow || "",
    opacity: table.opacity || "",
    overflow: table.overflow || "visible",
    overflowX: table.overflowX || "hidden",
    overflowY: table.overflowY || "hidden",
    cursor: table.cursor || "auto",

    scale: isHovered ? "1.01" : "1",

    captionSide: table.captionSide || "top",
    emptyCells: table.emptyCells || "show",
    tableLayout: table.tableLayout || "auto",
  };

  const cellStyle = {
    padding: table.cellPadding || "0",

    borderTop: table.borderTopCell || table.borderCell || "none",
    borderRight: table.borderRightCell || table.borderCell || "none",
    borderBottom: table.borderBottomCell || table.borderCell || "none",
    borderLeft: table.borderLeftCell || table.borderCell || "none",
    borderRadius: table.borderRadiusCell || "0px",
    color: table.Cellcolor || table.color || "#000000",
    backgroundColor:
      table.CellbackgroundColor || table.backgroundColor || "transparent",

    fontSize: table.fontSizeCell || "inherit",
    fontFamily: table.fontFamilyCell || "inherit",
    fontWeight: table.fontWeightCell || "normal",
    fontStyle: table.fontStyleCell || "normal",
    textDecoration: table.textDecorationCell || "none",
    textTransform: table.textTransformCell || "none",
    textAlign: table.textAlignCell || "left",
  };

  const headerStyle = {
    padding: table.headerPadding || "0px",

    borderTop: table.borderTopHeader || table.borderHeader || "none",
    borderRight: table.borderRightHeader || table.borderHeader || "none",
    borderBottom: table.borderBottomHeader || table.borderHeader || "none",
    borderLeft: table.borderLeftHeader || table.borderHeader || "none",

    borderRadius: table.borderRadiusHeader || "0px",
    color: table.Headercolor || table.color || "#000000",
    backgroundColor:
      table.HeaderbackgroundColor || table.backgroundColor || "transparent",

    fontSize: table.fontSizeHeader || "inherit",
    fontFamily: table.fontFamilyHeader || "inherit",
    fontWeight: table.fontWeightHeader || "normal",
    fontStyle: table.fontStyleHeader || "normal",
    textDecoration: table.textDecorationHeader || "none",
    textTransform: table.textTransformHeader || "none",
    textAlign: table.textAlignHeader || "left",
  };

  const defaultHeaders = ["Header 1", "Header 2", "Header 3"];
  const defaultRows = [
    ["Row 1, Cell 1", "Row 1, Cell 2", "Row 1, Cell 3"],
    ["Row 2, Cell 1", "Row 2, Cell 2", "Row 2, Cell 3"],
    ["Row 3, Cell 1", "Row 3, Cell 2", "Row 3, Cell 3"],
  ];

  const headers = table.headers ? table.headers.split(",") : defaultHeaders;
  const rows = table.data
    ? table.data
        .split(";")
        .map((row) => (typeof row === "string" ? row.split(",") : []))
    : defaultRows;
  // Parse colspan and rowspan data
  const spanMap = new Map();
  if (table.colspanRowspan) {
    table.colspanRowspan.split(";").forEach((span) => {
      const [row, col, colspan, rowspan] = span.split(",").map(Number);
      spanMap.set(`${row},${col}`, { colspan, rowspan });
    });
  }

  // Function to check if a cell should be rendered
  const shouldRenderCell = (rowIndex, cellIndex) => {
    for (let i = 0; i < rowIndex; i++) {
      const span = spanMap.get(`${i},${cellIndex}`);
      if (span && span.rowspan > rowIndex - i) {
        return false;
      }
    }
    return true;
  };

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          {headers.map((header, index) => {
            const span = spanMap.get(`0,${index}`);
            return shouldRenderCell(0, index) ? (
              <th
                key={index}
                style={headerStyle}
                colSpan={span ? span.colspan : 1}
                rowSpan={span ? span.rowspan : 1}
              >
                {header}
              </th>
            ) : null;
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => {
              const span = spanMap.get(`${rowIndex + 1},${cellIndex}`);
              return shouldRenderCell(rowIndex + 1, cellIndex) ? (
                <td
                  key={cellIndex}
                  style={cellStyle}
                  colSpan={span ? span.colspan : 1}
                  rowSpan={span ? span.rowspan : 1}
                >
                  {cell}
                </td>
              ) : null;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default TableComponent;
